import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type Announce = (text: string) => void;

const PriceAnnouncerContext = createContext<Announce>(() => {});

/**
 * One polite live region for the whole price table (R13b): cells report
 * "Saved" or the error through it instead of each carrying its own
 * region, so a screen reader hears one message per save. The span is
 * keyed by a counter so a repeated message is a DOM change again and gets
 * announced a second time.
 */
export function PriceTableAnnouncer({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<{ text: string; seq: number }>({
    text: '',
    seq: 0,
  });

  const announce = useCallback<Announce>((text) => {
    setMessage((previous) => ({ text, seq: previous.seq + 1 }));
  }, []);

  const value = useMemo(() => announce, [announce]);

  return (
    <PriceAnnouncerContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-testid="price-table-status"
      >
        <span key={message.seq}>{message.text}</span>
      </div>
    </PriceAnnouncerContext.Provider>
  );
}

/** Announces a short save status; a no-op outside the provider. */
export function usePriceAnnouncer(): Announce {
  return useContext(PriceAnnouncerContext);
}
